import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, inr } from "@/api";
import { COMPANY_HEX } from "@/store";
import { Textarea } from "@/components/ui/textarea";
import { Star, CheckCircle2, Circle, Sparkles } from "lucide-react";

export default function Track() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [rated, setRated] = useState(false);

  useEffect(() => {
    axios.get(`${API}/track/${token}`)
      .then((r) => { setData(r.data); if (r.data.job.rating) setRated(true); })
      .catch(() => setErr("This tracking link is invalid."));
  }, [token]);

  const submitRating = async () => {
    try {
      await axios.post(`${API}/feedback/${token}`, { rating, comment });
      setRated(true);
    } catch (e) { /* already rated */ setRated(true); }
  };

  if (err) return <div className="min-h-screen bg-background text-danger-foreground flex items-center justify-center p-6" data-testid="track-error">{err}</div>;
  if (!data) return <div className="min-h-screen bg-background text-muted-foreground flex items-center justify-center">Loading…</div>;

  const { job, team_leader, bill } = data;
  const accent = COMPANY_HEX[job.company] || "hsl(var(--primary))";
  const steps = [
    { label: "Booked", done: true },
    { label: "Team on the way", done: ["In Progress", "Completed", "Closed"].includes(job.status) },
    { label: "Work completed", done: ["Completed", "Closed"].includes(job.status) },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground flex justify-center p-5" data-testid="track-page">
      <div className="w-full max-w-md space-y-5 py-6">
        <div className="flex items-center gap-2.5 justify-center">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: accent }}>
            <Sparkles size={17} className="text-foreground" />
          </div>
          <span className="font-heading font-bold">{job.company}</span>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-5 text-center">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Your booking</div>
          <div className="font-heading font-bold text-xl mt-1">{job.service}</div>
          <div className="text-xs text-muted-foreground font-mono mt-1">{job.job_no}</div>
          {job.scheduled_date && (
            <div className="mt-3 inline-block px-4 py-2 rounded-full text-sm font-semibold" style={{ background: `${accent}22`, color: accent }} data-testid="track-schedule">
              {job.scheduled_date}{job.scheduled_start_time ? ` · ${job.scheduled_start_time}` : ""}
            </div>
          )}
        </div>

        <div className="bg-surface border border-border rounded-2xl p-5" data-testid="track-steps">
          {steps.map((s) => (
            <div key={s.label} className="flex items-center gap-3 py-2">
              {s.done ? <CheckCircle2 size={20} style={{ color: accent }} /> : <Circle size={20} className="text-foreground" />}
              <span className={s.done ? "font-medium" : "text-muted-foreground"}>{s.label}</span>
            </div>
          ))}
        </div>

        {team_leader && (
          <div className="bg-surface border border-border rounded-2xl p-5 flex items-center gap-4" data-testid="track-team">
            <div className="w-12 h-12 rounded-full flex items-center justify-center font-heading font-extrabold text-lg overflow-hidden" style={{ background: `${accent}33`, color: accent }}>
              {team_leader.picture ? <img src={team_leader.picture} alt="" className="w-full h-full object-cover" /> : team_leader.name?.[0]}
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest">Your team leader</div>
              <div className="font-heading font-bold">{team_leader.name}</div>
            </div>
          </div>
        )}

        {bill && (
          <div className="bg-surface border border-border rounded-2xl p-5" data-testid="track-invoice">
            <div className="text-xs text-muted-foreground uppercase tracking-widest">Invoice {bill.invoice_no}</div>
            <div className="flex items-end justify-between mt-1">
              <span className="font-mono text-2xl font-bold">{inr(bill.total_with_gst)}</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${bill.payment_status === "Full" ? "bg-success/20 text-success" : "bg-warning/20 text-warning"}`}>
                {bill.payment_status === "Full" ? "Paid" : "Payment " + bill.payment_status}
              </span>
            </div>
          </div>
        )}

        {["Completed", "Closed"].includes(job.status) && !rated && (
          <div className="bg-surface border border-border rounded-2xl p-5 space-y-4" data-testid="track-rating">
            <div className="font-heading font-bold text-center">How was the service?</div>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} data-testid={`track-star-${s}`} onClick={() => setRating(s)} className="transition-transform hover:scale-110">
                  <Star size={32} className={s <= rating ? "text-warning-foreground" : "text-foreground"} fill={s <= rating ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
            <Textarea data-testid="track-comment" placeholder="Comments (optional)" value={comment} onChange={(e) => setComment(e.target.value)}
              className="bg-background border-border" />
            <button data-testid="track-submit-rating" onClick={submitRating} disabled={!rating}
              className="w-full py-3.5 rounded-xl font-bold text-foreground disabled:opacity-40" style={{ background: accent }}>
              Submit
            </button>
          </div>
        )}
        {rated && <div className="text-center text-sm" style={{ color: accent }} data-testid="track-rated">Thanks for your feedback!</div>}
      </div>
    </div>
  );
}
